import React, { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { Checkbox } from '../..';
import { getChildrenByType, getChildrenProps } from '../../utils';
import Pagination from '../pagination/Pagination';
import { ListInstance, useList } from './useList';

export type Column = {
    header?: string;
    alignment?: 'left' | 'right' | 'center';
    gridColumnWidth?: string;
};

type ListConfig = {
    columns: Column[];
    listInstance: ListInstance;
};

const ListContext = React.createContext<ListConfig>({ columns: [], listInstance: undefined });
export type ListItemProps = {
    id: string;
    children: ReactNode | ReactNode[];
};

export type ListProps = {
    children: ReactNode | ReactNode[];
    totalRows: number;
    rowLimits: number[];
    columns: Column[];
    isLoading?: boolean;
    onPageChange?: (page: number) => void;
    onRowLimitChange?: (limit: number) => void;
    onSelect?: (ids: string[]) => void;
};

function List({
    children,
    columns,
    totalRows,
    rowLimits,
    onPageChange,
    onRowLimitChange,
    onSelect,
    isLoading = false,
}: ListProps): JSX.Element {
    const _children = useMemo(() => getChildrenByType(children, ListItem), [children]);
    const _childrenProps = useMemo(() => getChildrenProps<ListItemProps>(_children), [_children]);
    const ids = useMemo(() => _childrenProps.map((p) => p.id), [_childrenProps]);

    const listConfig = useMemo(() => ({ itemIds: ids, currentViewedItems: ids }), [ids]);
    const list = useList(listConfig);

    const contextConfig = { columns, listInstance: list };

    const gridTemplateColumns = '25px ' + columns.map((column) => column.gridColumnWidth || '1fr').join(' ');

    useEffect(() => {
        onSelect(list.checkedItemsInView);
    }, [list.checkedItemsInView]);

    return (
        <ListContext.Provider value={contextConfig}>
            {/* List Header */}
            <div className="pb-2 border-b dark:border-white/[0.12] grid gap-8" style={{ gridTemplateColumns }}>
                <div className="flex justify-end">
                    <Checkbox
                        checked={list.allChecked}
                        indeterminate={list.someChecked}
                        onChange={(e): void => {
                            list.toggleAllChecked();
                        }}
                    />
                </div>
                {columns.map((column, idx) => {
                    return (
                        <div key={idx}>
                            <p
                                className="font-medium text-xs text-neutral-600 dark:text-white/[0.87]"
                                style={{ textAlign: column.alignment || 'left' }}
                            >
                                {column.header}
                            </p>
                        </div>
                    );
                })}
            </div>
            {isLoading ? (
                <div className="w-full flex justify-center h-[250px] items-center">Loading...</div>
            ) : (
                <div className="w-full">{_children}</div>
            )}
            {totalRows > ids.length && (
                <div className="mt-4">
                    <Pagination
                        page={1}
                        rowLimit={10}
                        rowLimits={rowLimits}
                        total={isLoading ? 0 : totalRows}
                        onRowLimitChange={(limit): void => typeof onRowLimitChange === 'function' && onRowLimitChange(limit)}
                        onPageChange={(page): void => typeof onPageChange === 'function' && onPageChange(page)}
                    />
                </div>
            )}
        </ListContext.Provider>
    );
}

function ListItem({ children, id }: ListItemProps): JSX.Element {
    const { listInstance, columns } = useContext(ListContext);
    const gridTemplateColumns = '25px ' + columns.map((column) => column.gridColumnWidth || '1fr').join(' ');

    return (
        <div className="grid gap-8 border-b dark:border-white/[0.12] py-1" style={{ gridTemplateColumns }}>
            <div className="py-1 flex justify-end">
                <Checkbox
                    checked={listInstance.getItemCheckedState(id)}
                    onChange={(e): void => listInstance.toggleChecked(id, e.target.checked)}
                />
            </div>

            {React.Children.map(children, (child, idx) => {
                return <>{child}</>;
            })}
        </div>
    );
}

List.ListItem = ListItem;
// List.whyDidYouRender = true;

export default List;
